<template>
  <div class="manage-entry-page my-3">
    <b-breadcrumb class="align-items-center">
      <b-breadcrumb-item :to="{ name: 'ManageAll' }">Manage</b-breadcrumb-item>
      <b-breadcrumb-item :to="{ name: 'ManageEntryList', params: { entryType } }">
        <template v-if="listSchema && listSchema.page && listSchema.page.breadcrumb">
          <template v-if="typeof listSchema.page.breadcrumb === 'function'">{{ listSchema.page.call(this) }}</template>
          <template v-else>{{ listSchema.page.breadcrumb }}</template>
        </template>
        <template v-else-if="modelData && modelData.name">
          {{ modelData.name }}
        </template>
        <template v-else>Entries</template>
      </b-breadcrumb-item>
      <b-breadcrumb-item active>
        <template v-if="editSchema && editSchema.page && editSchema.page.breadcrumb">
          <template v-if="typeof editSchema.page.breadcrumb === 'function'">{{ editSchema.page.breadcrumb.call(this) }}</template>
          <template v-else>{{ editSchema.page.breadcrumb }}</template>
        </template>
        <template v-else>{{ isCreate ? 'New' : 'Edit' }}</template>
      </b-breadcrumb-item>
      <div class="flex-fill"/>
      <div class="d-none d-sm-flex">
        <b-btn class="rounded mr-2" @click="() => showJSON = !showJSON" size="sm" variant="outline-dark" v-if="isSuperAdmin">
          <font-awesome-icon icon="code"/> {{showJSON ? 'Hide' : 'View'}}
        </b-btn>
        <b-btn class="rounded mr-2" v-if="!isCreate" size="sm" variant="outline-danger">
          <font-awesome-icon icon="ban"/> Disable
        </b-btn>
        <b-btn class="rounded" @click="() => isCreate ? createEntry() : updateEntry()" size="sm" variant="outline-primary">
          <font-awesome-icon icon="save"/> Save
        </b-btn>
      </div>
    </b-breadcrumb>

    <b-alert v-if="alert" class="d-flex justify-content-start" show :variant="alert.variant || 'info'">
      <p class="m-0">{{ alert.message || `${alert}` }}</p>
      <div class="flex-fill"/>
      <b-btn class="py-0" @click.prevent="() => alert = null" size="sm" variant="transparent">
        <font-awesome-icon icon="times"/> <span class="sr-only">Dismiss</span>
      </b-btn>
    </b-alert>

    <no-ssr v-if="value">
      <b-row class="bg-white border my-3 mx-0" v-if="isSuperAdmin && showJSON">
        <b-col class="pt-3" md="6">
          <vue-json-pretty :data="data" show-length show-line highlight-mouseover-node :deep="2"/>
        </b-col>
        <b-col class="pt-3 bg-light" md="6">
          <vue-json-pretty :data="{ value, update }" show-length show-line highlight-mouseover-node :deep="3"/>
        </b-col>
      </b-row>
      <b-row class="bg-white border my-3 mx-0" v-if="editSchema && editSchema.form">
        <b-col class="pt-3">
          <component v-if="editSchema.form.$main && typeof editSchema.form.$main.render === 'function'"
            :is="editSchema.form.$main" :data="data" @update="updateValue" v-model="value"/>
          <vue-form-bricks v-else-if="editSchema.form.$main" :data="data" :layout="editSchema.form.$main" @update="updateValue" v-model="value"/>
        </b-col>
        <b-col md="4" lg="3" class="bg-light">
          <component v-if="editSchema.form.$sidebar && typeof editSchema.form.$sidebar.render === 'function'"
            :is="editSchema.form.$sidebar" :data="data" @update="updateValue" v-model="value"/>
          <vue-form-bricks v-else-if="editSchema.form.$sidebar" :data="data" :layout="editSchema.form.$sidebar" @update="updateValue" v-model="value"/>
          <div class="border-top mt-1 py-3 d-flex d-sm-none flex-row justify-content-end align-items-center">
            <b-btn v-if="!isCreate" size="sm" variant="outline-danger">
              <font-awesome-icon icon="ban"/>&nbsp;Disable
            </b-btn>
            <b-btn class="ml-2" size="sm" @click="() => isCreate ? createEntry() : updateEntry()" variant="primary">
              <font-awesome-icon icon="save"/>&nbsp;Save
            </b-btn>
          </div>
        </b-col>
      </b-row>
    </no-ssr>
  </div>
</template>

<script>
import _capitalize from 'lodash/capitalize';
import _set from 'lodash/set';
import VueFormBricks from '@src/plugins/vue-form-bricks';
import VueJsonPretty from 'vue-json-pretty';
import models from '@/models';
import { mapRoot } from '@src/mixins/root';

export default {
  name: 'ManageView',
  props: {
    entryType: {
      type: String,
      validate: key => models.hasOwnProperty(key),
    },
    entryID: String,
  },
  components: {
    VueFormBricks,
    VueJsonPretty,
  },
  data: () => ({
    alert: null,
    data: null,
    value: null,
    update: {},
    showJSON: false,
    hasChanges: false,
  }),
  filters: {
    capitalize: value => _capitalize(value),
  },
  created() {
    if (this.isCreate) {
      this.value = {};
    }
  },
  apollo: {
    entry: {
      query() {
        const { apolloReadEntry: { query } = {} } = this.editSchema || {};
        this.$assert(query, new Error('Expected apollo.query to be a graphql-tag or string in editSchema'));
        return query;
      },
      variables() {
        const { entryID } = this;
        return { entryID };
      },
      async result({ loading, data, errors }) {
        if (!loading && data && data.company && data.company.id) {
          try {
            const { value, ...rest } = data.company;
            this.data = { ...rest, value: JSON.parse(JSON.stringify(value)) };
            this.value = value;

            const { apolloReadEntry: { result } = {} } = this.editSchema || {};
            if (typeof result === 'function') {
              await result.call(this);
            }
          } catch (err) {
            this.$notifyErr(err);
          }
        }
      },
      error(err) {
        this.alert = {
          message: err.message || `${err}`,
          variant: 'danger',
        };
      },
      skip() {
        return this.isCreate;
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      manual: true,
    },
  },
  metaInfo() {
    const meta = {
      title: 'Manage Entry',
    };

    if (this.editSchema && this.editSchema.page && this.editSchema.page.title) {
      if (typeof this.editSchema.page.title === 'function') {
        meta.title = this.editSchema.page.title.call(this);
      } else {
        meta.title = this.editSchema.page.title;
      }
    }

    return meta;
  },
  beforeRouteLeave(to, from, next) {
    if (process.env.VUE_ENV === 'client') {
      if (this.hasChanges && !confirm(this.$t('manage.confirm-leave'))) {
        return next(false);
      }

      const { params: { companyID: oldCompanyID } = {} } = from;
      const { params: { companyID: newCompanyID, entryType } = {} } = to;
      // In this block, we're switching company to the same route
      if (newCompanyID && newCompanyID !== oldCompanyID && to.name === from.name) {
        return next({ name: 'ManageEntryList', params: { entryType } });
      }
    }

    next();
  },
  beforeMount() {
    window.addEventListener('beforeunload', this.onWindowClose);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onWindowClose);
  },
  computed: {
    ...mapRoot([ 'isSuperAdmin', 'isRecruiterAdmin', 'isRecruiter' ]),
    modelData() {
      const { [this.entryType]: modelData = null } = models || {};
      return modelData;
    },
    listSchema() {
      const { listSchema = null } = this.modelData || {};
      return listSchema;
    },
    editSchema() {
      const { editSchema = null } = this.modelData || {};
      return editSchema;
    },
    entryLabel() {
      return this.entryType.toLowerCase().substr(0, this.entryType.length - 1);
    },
    isCreate() {
      return this.entryID === 'new';
    },
  },
  methods: {
    updateValue({ key, value }) {
      this.hasChanges = true;
      _set(this.update, key, value);
    },
    async createEntry() {
      const { entryLabel } = this;
      try {
        this.alert = null;
        this.$assert(this.isCreate, new Error('Invalid path for creating entries'));

        const { apolloCreateEntry: { mutation, validate } = {} } = this.editSchema || {};
        this.$assert(mutation, new Error('Expected apolloWriteEntry.mutation to be a graphql-tag or string in editSchema'));

        const { update: create } = this;
        if (typeof validate === 'function') {
          await validate.call(this, create, this.value);
        }

        this.$log.info({ entryID: 'new', create });

        const { data, errors } = await this.$apollo.mutate({
          mutation,
          variables: { create },
        });

        if (Array.isArray(errors) && errors.length) {
          errors.forEach(err => this.$notifyErr(err, { toaster: 'b-toaster-top-center' }));
        }

        this.$assert(data && data.value, new Error(`Failed to create ${entryLabel}`));
        const { id: entryID } = data.value;
        this.$assert(entryID, new Error(`Failed to create ${entryLabel} - missing ID`));

        // At this point, assume the data has been saved correctly!
        this.hasChanges = false;
        this.update = {};

        const { name, params } = this.$route;
        this.$router.replace({ name, params: { ...params, entryID } });

        // @TODO Any clever trick to stop Apollo from watching the variables?

        this.alert = {
          message: `Successfully created new ${entryLabel}`,
          variant: 'success',
        };
      } catch (err) {
        this.alert = {
          message: err.message || `${err}`,
          variant: 'danger',
        };
      }
    },
    async updateEntry() {
      const { entryLabel, entryID } = this;
      try {
        this.alert = null;
        this.$assert(!this.isCreate, new Error('Invalid path for creating entries'));

        const { apolloUpdateEntry: { mutation, validate } = {} } = this.editSchema || {};
        this.$assert(mutation, new Error('Expected apolloWriteEntry.mutation to be a graphql-tag or string in editSchema'));

        const { update } = this;
        if (typeof validate === 'function') {
          await validate.call(this, update, this.value);
        }

        this.$log.info({ entryID, update });

        const { data, errors } = await this.$apollo.mutate({
          mutation,
          variables: { entryID, update },
          errorPolicy: 'all',
        });

        if (Array.isArray(errors) && errors.length) {
          errors.forEach(err => this.$notifyErr(err, { toaster: 'b-toaster-top-center' }));
        }

        this.$assert(data && data.value, new Error(`Failed to update ${entryLabel}`));

        // At this point, assume the data has been saved correctly!
        this.hasChanges = false;
        this.update = {};
        this.value = data.value;

        const { apolloReadEntry: { result } = {} } = this.editSchema || {};
        if (typeof result === 'function') {
          await result.call(this);
        }

        this.alert = {
          message: `Successfully updated ${entryLabel}`,
          variant: 'success',
        };

        this.$nextTick(() => this.$scrollTo('#app'));
      } catch (err) {
        this.alert = {
          message: err.message || `${err}`,
          variant: 'danger',
        };
      }
    },
    onWindowClose($event) {
      if (this.hasChanges) {
        // @link https://jsfiddle.net/jbf4vL7h/29/
        const confirm = 'You have unsaved changes';
        $event.returnValue = confirm; // Gecko, Trident, Chrome 34+
        return confirm; // Gecko, WebKit, Chrome <34
      } else {
        return true;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/manage/view.vue